import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'

import { getUserAlias } from 'models/ProfileInfo/selectors'
import profileInfoActions from 'models/ProfileInfo/actions'

import { STRING, EVENT_TYPE } from 'resources'
import { sendAnalyticsEvent } from 'services/analytics'
import { getReklaimProtectEmailAddresses } from 'services/reklaimProtect'

import { ModalTitle } from 'components/ReklaimModal'

import {
  ModalDescription,
  CenteredContainer,
  EmailText,
  ResendEmailContainer,
  ResendEmailButton,
  FooterText,
  FooterButton,
} from './styles'

const RESEND_TIMEOUT_MS = 10000
const REFETCH_TIMEOUT_MS = 10000

const EmailAddressAddConfirmation = ({ pendingEmailAddress, setEmailAddressData, closeModal }) => {
  const dispatch = useDispatch()

  const userAlias = useSelector(getUserAlias)

  const [isFetchingEmailAddresses, setIsFetchingEmailAddresses] = useState(false)
  const [canResendVerificationEmail, setCanResendVerificationEmail] = useState(true)
  const [sendVerificationEmailFailed, setSendVerificationEmailFailed] = useState(false)

  const [canRefetchEmailAddresses, setCanRefetchEmailAddresses] = useState(true)

  const handleLetsFixThis = async () => {
    setIsFetchingEmailAddresses(true)
    const updatedEmailAddressData = await getReklaimProtectEmailAddresses(userAlias)
    if (updatedEmailAddressData) {
      setEmailAddressData(updatedEmailAddressData)
      if (updatedEmailAddressData.some((emailAddress) => emailAddress.emailAddress === pendingEmailAddress)) {
        closeModal()
        return
      }
    }
    setCanRefetchEmailAddresses(false)
    setIsFetchingEmailAddresses(false)
  }

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressView)
  }, [])

  useEffect(() => {
    if (!canResendVerificationEmail) {
      const timeoutId = setTimeout(() => {
        setCanResendVerificationEmail(true)
        setSendVerificationEmailFailed(false)
      }, RESEND_TIMEOUT_MS)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [canResendVerificationEmail])

  useEffect(() => {
    if (!canRefetchEmailAddresses) {
      const timeoutId = setTimeout(() => setCanRefetchEmailAddresses(true), REFETCH_TIMEOUT_MS)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [canRefetchEmailAddresses, setCanRefetchEmailAddresses])

  const onVerificationEmailResend = () => {
    sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalVerifyEmailAddressResendClick)
    setCanResendVerificationEmail(false)
    dispatch(
      profileInfoActions.addEmailAddressSendVerificationEmail.init(pendingEmailAddress, 'protect', (isSuccess) => {
        if (!isSuccess) {
          setSendVerificationEmailFailed(true)
        }
      }),
    )
  }

  return (
    <CenteredContainer>
      <ModalTitle>{STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.title}</ModalTitle>
      <ModalDescription>
        <span>{STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.description}</span>
        <EmailText>{pendingEmailAddress}</EmailText>
      </ModalDescription>
      <ResendEmailContainer>
        <ModalDescription>
          <span>{STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.resendLabel}</span>
          <ResendEmailButton onClick={onVerificationEmailResend} disabled={!canResendVerificationEmail}>
            {canResendVerificationEmail &&
              STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.resendButton}
            {!canResendVerificationEmail &&
              !sendVerificationEmailFailed &&
              STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.emailSent}
            {!canResendVerificationEmail &&
              sendVerificationEmailFailed &&
              STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.resendFailed}
          </ResendEmailButton>
        </ModalDescription>
      </ResendEmailContainer>
      <FooterText>
        <span>{STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.footerText}</span>
        <FooterButton onClick={handleLetsFixThis} disabled={!canRefetchEmailAddresses || isFetchingEmailAddresses}>
          {canRefetchEmailAddresses || isFetchingEmailAddresses
            ? STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.footerButtonText
            : STRING.account.addNewEmailAddressModal.emailAddressAddConfirmation.footerButtonTextChecked}
        </FooterButton>
      </FooterText>
    </CenteredContainer>
  )
}

EmailAddressAddConfirmation.propTypes = {
  pendingEmailAddress: PropTypes.string.isRequired,
  setEmailAddressData: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
}

export default EmailAddressAddConfirmation
